<template>
  <div v-if="visible" class="overlay-modal">
    <div class="modal-vincular">
      <h4 class="modal-titulo">Vincular formulario</h4>
      <p class="modal-sub">{{ nombreIndicador || `Indicador #${idIndicador}` }}</p>

      <p class="nota">
        <template v-if="esFormularioCompleto">
          Este formulario califica como una sola unidad: va completo a este
          indicador, sin repartir preguntas, y se califica con una sola nota.
        </template>
        <template v-else>
          El formulario de opinión es compartido: se crea una vez y cada indicador
          toma de él las preguntas que le corresponden. La nota del indicador será
          el promedio de las preguntas que marques.
        </template>
      </p>

      <!-- Contexto jerárquico del indicador -->
      <div v-if="ruta?.length || nombreIndicador" class="ruta-contexto">
        <div
          v-for="(nodo, i) in ruta"
          :key="i"
          class="ruta-nodo"
        >
          <span class="ruta-etiqueta">{{ nodo.etiqueta }}</span>
          <span class="ruta-sep">·</span>
          <span class="ruta-nombre">{{ nodo.nombre }}</span>
        </div>
        <div
          v-if="nombreIndicador"
          class="ruta-nodo ruta-indicador"
        >
          <span class="ruta-etiqueta">Indicador</span>
          <span class="ruta-sep">·</span>
          <span class="ruta-nombre">{{ nombreIndicador }}</span>
        </div>
      </div>

      <p v-if="cargando" class="info">Cargando formularios…</p>

      <template v-else>
        <div class="form-group">
          <label for="tipo-sel">Tipo de instrumento</label>
          <select id="tipo-sel" v-model="tipoFiltro">
            <option value="">Todos los tipos</option>
            <option v-for="t in tiposDisponibles" :key="t.valor" :value="t.valor">
              {{ t.etiqueta }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="form-sel">Formulario</label>
          <select id="form-sel" v-model="idInstrumento" @change="alCambiarFormulario">
            <option :value="null" disabled>Seleccione un formulario</option>
            <option v-for="f in formulariosFiltrados" :key="f.id_instrumento" :value="f.id_instrumento">
              {{ f.nombre }} · {{ f.tipo_etiqueta || f.tipo }} ({{ f.preguntas.length }} pregunta(s))
            </option>
          </select>
          <small v-if="!formularios.length" class="ayuda">
            No hay formularios con preguntas todavía. Créalo primero desde Instrumentos.
          </small>
          <small v-else-if="!formulariosFiltrados.length" class="ayuda">
            Ningún formulario de ese tipo. Cambia el filtro para ver los demás.
          </small>
        </div>

        <!-- Formulario completo: no hay preguntas que elegir. -->
        <div v-if="esFormularioCompleto && idInstrumento" class="seccion">
          <div class="seccion-head">
            <h5>Preguntas del formulario</h5>
            <span class="contador">{{ preguntasDelFormulario.length }} en total</span>
          </div>
          <ul class="lista-preguntas">
            <li v-for="p in preguntasOrdenadas" :key="p.id_pregunta" class="item-pregunta">
              <span class="pq-fija">
                {{ p.enunciado }}
                <span v-if="p.grupo_interes?.nombre" class="pq-grupo">{{ p.grupo_interes.nombre }}</span>
              </span>
            </li>
          </ul>
        </div>

        <div v-else-if="preguntasDelFormulario.length" class="seccion">
          <div class="seccion-head">
            <h5>Preguntas de este indicador</h5>
            <span class="contador">{{ seleccionadas.length }} marcada(s)</span>
          </div>

          <ul class="lista-preguntas">
            <template v-for="(p, idx) in preguntasOrdenadas" :key="p.id_pregunta">
              <li v-if="idx > 0 && p.id_indicador != null && preguntasOrdenadas[idx - 1].id_indicador == null" class="separador-asignadas">
                <span>Ya asignadas</span>
              </li>
              <li class="item-pregunta">
                <label :class="{ 'de-otro': esDeOtroIndicador(p) }">
                  <input type="checkbox" :value="p.id_pregunta" v-model="seleccionadas" />
                  <span class="pq-texto">{{ p.enunciado }}</span>
                  <span v-if="p.grupo_interes?.nombre" class="pq-grupo">{{ p.grupo_interes.nombre }}</span>
                  <span v-if="esDeOtroIndicador(p)" class="pq-aviso">
                    ya es de: {{ p.indicador?.enunciado }}
                  </span>
                </label>
              </li>
            </template>
          </ul>

          <p v-if="hayRobadas" class="aviso-robo">
            Marcaste preguntas que hoy pertenecen a otro indicador. Si guardas,
            pasarán a este.
          </p>
        </div>

        <p v-else-if="idInstrumento" class="info">Ese formulario no tiene preguntas.</p>

        <p v-if="error" class="error">{{ error }}</p>
      </template>

      <div class="botones">
        <button class="btn salir" :disabled="guardando" @click="$emit('cerrar')">Cancelar</button>
        <button
          class="btn guardar"
          :disabled="guardando || !idInstrumento"
          @click="guardar"
        >
          {{ guardando ? 'Guardando…' : 'Vincular' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Vinculación de un formulario existente a un indicador.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, watch } from 'vue'
import type { FormularioDisponible, PreguntaDeFormulario } from '@/services/IndicadorFormularioService'
import {
  getFormulariosDisponiblesService,
  vincularFormularioService
} from '@/services/IndicadorFormularioService'

interface NodoRuta { etiqueta: string; nombre: string }

const props = defineProps<{
  visible: boolean
  idIndicador: number | null
  nombreIndicador?: string | null
  ruta?: NodoRuta[]
}>()

const emit = defineEmits<{
  (e: 'cerrar'): void
  (e: 'vinculado', mensaje: string): void
}>()

const formularios = ref<FormularioDisponible[]>([])
const idInstrumento = ref<number | null>(null)
const seleccionadas = ref<number[]>([])
const tipoFiltro = ref('')
const cargando = ref(false)
const guardando = ref(false)
const error = ref('')

const formularioActual = computed(() =>
  formularios.value.find(x => x.id_instrumento === idInstrumento.value)
)

const preguntasDelFormulario = computed<PreguntaDeFormulario[]>(
  () => formularioActual.value?.preguntas ?? []
)

const preguntasOrdenadas = computed<PreguntaDeFormulario[]>(() => {
  return [...preguntasDelFormulario.value].sort((a, b) => {
    const aAsignada = a.id_indicador != null ? 1 : 0
    const bAsignada = b.id_indicador != null ? 1 : 0
    return aAsignada - bAsignada
  })
})

/**
 * El formulario entero evalúa este indicador: no se eligen preguntas y la
 * nota es una sola para todo.
 */
const esFormularioCompleto = computed(
  () => formularioActual.value?.modalidad === 'formulario_completo'
)

/** Tipos presentes en los formularios, deduplicados por etiqueta visible. */
const tiposDisponibles = computed(() => {
  const vistos = new Set<string>()
  const result: { valor: string; etiqueta: string }[] = []
  for (const f of formularios.value) {
    const etiqueta = (f.tipo_etiqueta || f.tipo || '').trim()
    if (!etiqueta || vistos.has(etiqueta)) continue
    vistos.add(etiqueta)
    result.push({ valor: etiqueta, etiqueta })
  }
  return result
})

const formulariosFiltrados = computed(() =>
  tipoFiltro.value
    ? formularios.value.filter(f => (f.tipo_etiqueta || f.tipo || '').trim() === tipoFiltro.value)
    : formularios.value
)

function esDeOtroIndicador(p: PreguntaDeFormulario): boolean {
  return p.id_indicador != null && p.id_indicador !== props.idIndicador
}

const hayRobadas = computed(() =>
  preguntasDelFormulario.value.some(p => seleccionadas.value.includes(p.id_pregunta) && esDeOtroIndicador(p))
)

watch(
  () => [props.visible, props.idIndicador] as const,
  async ([visible]) => {
    if (!visible || !props.idIndicador) return
    cargando.value = true
    error.value = ''
    idInstrumento.value = null
    seleccionadas.value = []
    tipoFiltro.value = ''
    try {
      formularios.value = await getFormulariosDisponiblesService(props.idIndicador)
    } finally {
      cargando.value = false
    }
  },
  { immediate: true }
)

/** Al elegir formulario se premarcan las preguntas que ya son de este indicador. */
function alCambiarFormulario() {
  seleccionadas.value = esFormularioCompleto.value
    ? []
    : preguntasDelFormulario.value
      .filter(p => p.id_indicador === props.idIndicador)
      .map(p => p.id_pregunta)
}

async function guardar() {
  if (!props.idIndicador || !idInstrumento.value) return
  guardando.value = true
  error.value = ''
  try {
    // En formulario completo no se mandan preguntas: va entero al indicador.
    const res = await vincularFormularioService(
      props.idIndicador,
      idInstrumento.value,
      esFormularioCompleto.value ? undefined : seleccionadas.value
    )
    if (!res.ok) {
      error.value = res.error ?? 'No se pudo vincular.'
      return
    }
    emit('vinculado', res.mensaje ?? 'Formulario vinculado.')
    emit('cerrar')
  } finally {
    guardando.value = false
  }
}
</script>

<style scoped>
.overlay-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1.5rem;
}

.modal-vincular {
  background: #fff;
  border-radius: 10px;
  padding: 1.4rem;
  width: 100%;
  max-width: 620px;
  max-height: 86vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.22);
  font-family: var(--font-family, inherit);
}

.modal-titulo {
  margin: 0 0 0.2rem;
  font-size: 1.1rem;
  font-weight: 700;
}

.modal-sub {
  margin: 0 0 0.8rem;
  font-size: 0.85rem;
  color: #666;
}

.nota {
  margin: 0 0 1.1rem;
  padding: 0.6rem 0.75rem;
  border-left: 3px solid #eea222;
  background: #fdf6e7;
  border-radius: 4px;
  font-size: 0.8rem;
  line-height: 1.45;
  color: #5c4a1e;
}

.ruta-contexto {
  margin: 0 0 1.1rem;
  padding: 0.6rem 0.75rem;
  background: #f0f5fb;
  border: 1px solid #ccdaeb;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.ruta-nodo {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  font-size: 0.8rem;
  line-height: 1.4;
}

.ruta-etiqueta {
  font-weight: 700;
  color: #0e69a0;
  white-space: nowrap;
  flex-shrink: 0;
}

.ruta-sep {
  color: #94a3b8;
  flex-shrink: 0;
}

.ruta-nombre {
  color: #1a1a1a;
}

.ruta-indicador .ruta-etiqueta {
  color: #1a56a0;
}

.ruta-indicador .ruta-nombre {
  font-style: italic;
}

.pq-grupo {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  background: #e8f0fb;
  color: #1a56a0;
  border: 1px solid #bdd0ef;
  margin-left: 6px;
  white-space: nowrap;
  vertical-align: middle;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
}

.form-group label {
  font-size: 0.82rem;
  font-weight: 600;
}

select {
  padding: 0.55rem 0.7rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.85rem;
}

.ayuda {
  font-size: 0.75rem;
  color: #888;
}

.seccion-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.seccion-head h5 {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 600;
}

.contador {
  background: #0e69a0;
  color: #fff;
  border-radius: 10px;
  padding: 0.1rem 0.55rem;
  font-size: 0.72rem;
}

.lista-preguntas {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 260px;
  overflow-y: auto;
}

.item-pregunta label {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  border: 1px solid #ececec;
  border-radius: 6px;
  margin-bottom: 0.35rem;
  font-size: 0.82rem;
  cursor: pointer;
  background: #fdfdfd;
}

.item-pregunta label.de-otro {
  background: #fff8f0;
  border-color: #f0d9b8;
}

.pq-texto {
  flex: 1;
}

.pq-fija {
  display: block;
  padding: 0.4rem 0.5rem;
  border: 1px solid #ececec;
  border-radius: 6px;
  margin-bottom: 0.35rem;
  font-size: 0.82rem;
  background: #f7f9fb;
  color: #444;
}

.pq-aviso {
  font-size: 0.7rem;
  color: #b06a00;
  white-space: nowrap;
}

.separador-asignadas {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.6rem 0 0.4rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.separador-asignadas::before,
.separador-asignadas::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e5e5;
}

.aviso-robo {
  margin: 0.5rem 0 0;
  padding: 0.5rem 0.7rem;
  background: #fff8f0;
  border-left: 3px solid #eea222;
  border-radius: 4px;
  font-size: 0.78rem;
  color: #5c4a1e;
}

.info {
  font-size: 0.85rem;
  color: #666;
}

.error {
  margin-top: 0.6rem;
  padding: 0.5rem 0.7rem;
  background: #f8d7da;
  color: #721c24;
  border-radius: 5px;
  font-size: 0.8rem;
}

.botones {
  display: flex;
  justify-content: flex-end;
  gap: 0.7rem;
  margin-top: 1.1rem;
}

.btn {
  padding: 8px 20px;
  height: 38px;
  font-weight: 600;
  font-size: 0.83rem;
  font-family: inherit;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.salir {
  background: #fff;
  border: 1px solid #ccc;
}

.btn.guardar {
  background: #eea222;
  color: #000;
}

.btn.guardar:not(:disabled):hover {
  background: #0e69a0;
  color: #fff;
}
</style>
